import { useId, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowLeft01IconStroke, ArrowRight01IconStroke } from '@/components/icons';
import { T } from '@/util/Localize';
import { WeatherIcons, weatherForDate, type CalendarWeather } from '../cart-weather';
import {
    addDays,
    formatSlotTime,
    isBookableDate,
    localToday,
    weekStartOf,
    type AvailabilityDay,
    type AvailabilityLocation,
    type AvailabilityTeamMode,
    type SlotCandidate
} from './availability-api';
import { useAvailabilityWeek } from './use-availability';

/** What the user picked: a start time, or — on a date-only calendar — a whole day (`minute: null`). */
export interface AvailabilitySelection {
    date: string;
    minute: number | null;
    /**
     * TEAM MODE only: the members who can take the picked slot. The picker does not choose between
     * them — the host does (`resolveSlotMember`) and holds the slot for that ONE member.
     */
    candidates?: SlotCandidate[];
    /** TEAM MODE only: the week's open-order counts, which `teamselect` 2 resolves with. */
    openOrders?: Record<number, number>;
}

export interface AvailabilityPickerProps {
    brandId: number;
    /** The one member whose calendar is shown. Not needed when `teamMemberIds` asks for a team. */
    teamMemberId?: number;
    /**
     * TEAM MODE (`cart-gcal.team` 1 or 2): one merged calendar over these members. Each selection
     * is then reported with its `candidates`. Takes effect only together with `team` 1 or 2.
     */
    teamMemberIds?: readonly number[];
    team?: AvailabilityTeamMode;
    /** `sWeekMask` in wire form (`buildWeekMask`): weekdays a service area closes per member. */
    weekMask?: string;
    /** The shopper's favourite members (`nFavorite`), team mode only. */
    favoriteIds?: readonly number[];
    /** Minutes on site; `-1` for a twilight / date-only product. */
    duration: number;
    /** The appointment's coordinates, when known (drive time is part of the answer). */
    location?: AvailabilityLocation | null;
    /** Tasks being moved, so their own time is not shown as busy. */
    ignoreTaskIds?: readonly number[];
    /** The host's own hold ids, so its hold does not hide its own slot. */
    ignoreReservationIds?: readonly string[];
    /** Set for a guest only: sends `hd-download-guid` and no session. */
    downloadGuid?: string;
    /** The brand's "today" as `YYYY-MM-DD`. Defaults to the browser's date. */
    today?: string;
    value: AvailabilitySelection | null;
    onSelect: (selection: AvailabilitySelection) => void;
    /** The slot the host is still confirming (taking a hold on); it spins and the rest wait. */
    pending?: AvailabilitySelection | null;
    /** Slots the host knows are gone (e.g. from a refused hold) but the solver may still list. */
    isSlotDisabled?: (date: string, minute: number) => boolean;
    disabled?: boolean;
    /** Accessible name for the whole control. */
    label?: string;
    /**
     * ROBO-846: the forecast by ISO date and the brand's units. Each day with rain, snow or wind
     * in it gets a small icon beside its date. Absent, the calendar draws exactly as it always did.
     */
    weather?: CalendarWeather;
}

/**
 * Read by a screen reader, not drawn.
 * ★ '1px', not 1: in `sx` a unitless width or height of 1 means 100%, which made every hidden label as wide
 * as the page and pushed a phone-width page 131px sideways (ROBO-694, caught by its mobile spec).
 */
const visuallyHidden = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap'
} as const;

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
];

function parts(date: string) {
    const [year, month, day] = date.split('-').map(Number);
    const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    return { year, month: MONTHS[month - 1], day, weekday: WEEKDAYS[weekday] };
}

/** `Monday, April 5` — the spoken name of a day, used in every slot's accessible name. */
function longDay(date: string): string {
    const { weekday, month, day } = parts(date);
    return `${T(weekday)}, ${T(month)} ${day}`;
}

function weekRange(weekStart: string): string {
    const first = parts(weekStart);
    const last = parts(addDays(weekStart, 6));
    return `${T(first.month).slice(0, 3)} ${first.day} – ${T(last.month).slice(0, 3)} ${last.day}, ${last.year}`;
}

function sameSelection(
    a: AvailabilitySelection | null | undefined,
    date: string,
    minute: number | null
) {
    return Boolean(a) && a!.date === date && a!.minute === minute;
}

/** Everyone who can take any of a date-only day's entries, each once, at their best rank. */
function dayCandidates(day: AvailabilityDay): SlotCandidate[] {
    const best = new Map<number, number>();
    for (const slot of day.teamSlots ?? []) {
        for (const { id, rank } of slot.candidates) {
            const known = best.get(id);
            if (known === undefined || (rank > 0 && (known === 0 || rank < known))) {
                best.set(id, rank);
            }
        }
    }
    return Array.from(best, ([id, rank]) => ({ id, rank }));
}

/**
 * A week of open appointment times, with previous / next week paging — the React counterpart of
 * legacy `cart-gcal`, for one team member or (ROBO-694) one merged team calendar (see
 * `availability-api.ts` for what is replicated and what is left to the cart migration).
 *
 * It only OFFERS times. Taking a hold on the picked slot, the countdown and the save belong to the
 * host (`hooks/use-slot-hold`), which is why `value`, `pending` and `isSlotDisabled` are props:
 * the picker never decides on its own that a slot is taken.
 */
export function AvailabilityPicker({
    brandId,
    teamMemberId,
    teamMemberIds,
    team,
    weekMask,
    favoriteIds,
    duration,
    location,
    ignoreTaskIds,
    ignoreReservationIds,
    downloadGuid,
    today,
    value,
    onSelect,
    pending,
    isSlotDisabled,
    disabled = false,
    label,
    weather
}: AvailabilityPickerProps) {
    const todayDate = today ?? localToday();
    const firstWeek = weekStartOf(todayDate);
    const [weekStart, setWeekStart] = useState(firstWeek);
    const rangeId = useId();

    const { data, isPending, isFetching, isError, refetch } = useAvailabilityWeek({
        brandId,
        teamMemberId,
        teamMemberIds,
        team,
        weekMask,
        favoriteIds,
        duration,
        weekStart,
        location,
        ignoreTaskIds,
        ignoreReservationIds,
        downloadGuid
    });

    const isBusy = disabled || Boolean(pending);
    const canGoBack = weekStart > firstWeek;

    const days = useMemo(() => (data?.kind === 'open' ? data.days : []), [data]);
    const daysOut = data?.kind === 'open' ? data.daysOut : 0;
    const openOrders = data?.kind === 'open' ? data.openOrders : undefined;

    /** Team mode: a pick goes out with who can take it. Single mode: exactly as it always did. */
    const select = (day: AvailabilityDay, minute: number | null) => {
        if (day.teamSlots === undefined) {
            onSelect({ date: day.date, minute });
            return;
        }
        const candidates =
            minute === null
                ? dayCandidates(day)
                : (day.teamSlots.find(slot => slot.minute === minute)?.candidates ?? []);
        onSelect({ date: day.date, minute, candidates, openOrders: openOrders ?? {} });
    };
    const hasAnything = days.some(
        day =>
            isBookableDate(day.date, todayDate, daysOut) && (day.isOpenDay || day.slots.length > 0)
    );

    const renderDay = (day: AvailabilityDay) => {
        const { weekday, month, day: dayOfMonth } = parts(day.date);
        const isToday = day.date === todayDate;
        const isBookable = isBookableDate(day.date, todayDate, daysOut);
        const name = longDay(day.date);

        let body: React.ReactNode;
        if (isToday) {
            body = (
                <Typography variant="body2" color="text.secondary">
                    {T('Today')}
                </Typography>
            );
        } else if (isBookable && day.isOpenDay) {
            const isSelected = sameSelection(value, day.date, null);
            body = (
                <Button
                    size="small"
                    variant={isSelected ? 'contained' : 'outlined'}
                    aria-pressed={isSelected}
                    aria-label={`${T('Select this day')}: ${name}`}
                    disabled={isBusy}
                    onClick={() => select(day, null)}
                    sx={{ minHeight: 36 }}
                >
                    {isSelected ? T('Selected') : T('Select this day')}
                </Button>
            );
        } else if (isBookable && day.slots.length > 0) {
            body = (
                <Box
                    component="ul"
                    sx={{
                        listStyle: 'none',
                        m: 0,
                        p: 0,
                        display: 'flex',
                        flexWrap: 'wrap',
                        flexDirection: { xs: 'row', md: 'column' },
                        gap: 0.75
                    }}
                >
                    {day.slots.map(minute => {
                        const isSelected = sameSelection(value, day.date, minute);
                        const isPendingSlot = sameSelection(pending, day.date, minute);
                        const isGone = isSlotDisabled?.(day.date, minute) ?? false;
                        const time = formatSlotTime(minute);
                        return (
                            <li key={minute}>
                                <Button
                                    size="small"
                                    fullWidth
                                    variant={isSelected ? 'contained' : 'outlined'}
                                    aria-pressed={isSelected}
                                    aria-label={`${name} ${T('at')} ${time}`}
                                    loading={isPendingSlot}
                                    disabled={isGone || (isBusy && !isPendingSlot)}
                                    onClick={() => select(day, minute)}
                                    sx={{
                                        minHeight: 36,
                                        minWidth: { xs: 84, md: 0 },
                                        px: 1,
                                        whiteSpace: 'nowrap',
                                        ...(isGone ? { textDecoration: 'line-through' } : {})
                                    }}
                                >
                                    {time}
                                </Button>
                            </li>
                        );
                    })}
                </Box>
            );
        } else {
            body = (
                <Typography variant="body2" color="text.disabled">
                    <span aria-hidden="true">—</span>
                    <Box component="span" sx={visuallyHidden}>
                        {T('No times available')}
                    </Box>
                </Typography>
            );
        }

        return (
            <Box
                key={day.date}
                component="li"
                data-testid={`availability-day-${day.date}`}
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'row', md: 'column' },
                    gap: 1,
                    alignItems: { xs: 'flex-start', md: 'stretch' },
                    minWidth: 0,
                    py: { xs: 1, md: 0 },
                    borderTop: { xs: 1, md: 0 },
                    borderColor: 'divider'
                }}
            >
                <Box
                    sx={{
                        flex: { xs: '0 0 76px', md: '0 0 auto' },
                        textAlign: { xs: 'left', md: 'center' },
                        pt: { xs: 0.75, md: 0 }
                    }}
                >
                    <Typography
                        component="h4"
                        variant="subtitle2"
                        aria-label={name}
                        sx={{ lineHeight: 1.2 }}
                    >
                        {T(weekday).slice(0, 3)}
                        <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={
                                // The icons sit on the date's line and never wrap it (ROBO-846).
                                weather
                                    ? { display: 'block', whiteSpace: 'nowrap' }
                                    : { display: 'block' }
                            }
                        >
                            {/* Right-aligned on the date's line (Herb, 2026-10-24): floated, so first. */}
                            {weather && (
                                <Box component="span" sx={{ float: 'right', ml: 0.5 }}>
                                    <WeatherIcons
                                        day={weatherForDate(weather, day.date, todayDate)}
                                        units={weather.units}
                                        precipThreshold={weather.precipThreshold}
                                    />
                                </Box>
                            )}
                            {T(month).slice(0, 3)} {dayOfMonth}
                        </Typography>
                    </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>{body}</Box>
            </Box>
        );
    };

    let content: React.ReactNode;
    if (isError) {
        content = (
            <Alert
                severity="error"
                action={
                    <Button color="inherit" size="small" onClick={() => void refetch()}>
                        {T('Try again')}
                    </Button>
                }
            >
                {T('We could not check availability. Please try again.')}
            </Alert>
        );
    } else if (isPending) {
        content = (
            <Stack role="status" alignItems="center" spacing={1} sx={{ py: 4 }}>
                <CircularProgress size={28} />
                <Typography variant="body2">{T('Checking availability...')}</Typography>
            </Stack>
        );
    } else if (data.kind === 'unavailable') {
        // Legacy `errorWeeks`: the member's own message, or "Please call to schedule."
        content = (
            <Alert severity="info" data-testid="availability-unavailable">
                {data.message}
            </Alert>
        );
    } else if (!hasAnything) {
        content = (
            <Typography role="status" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                {T('No appointment times are available this week. Try the next week.')}
            </Typography>
        );
    } else {
        content = (
            <Box
                component="ul"
                aria-labelledby={rangeId}
                sx={{
                    listStyle: 'none',
                    m: 0,
                    p: 0,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(7, minmax(0, 1fr))' },
                    columnGap: 1,
                    rowGap: { xs: 0, md: 1 },
                    opacity: isFetching ? 0.6 : 1
                }}
            >
                {days.map(renderDay)}
            </Box>
        );
    }

    return (
        <Box
            role="group"
            aria-label={label ?? T('Appointment times')}
            aria-busy={isFetching}
            data-testid="availability-picker"
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
            >
                <IconButton
                    aria-label={T('Previous week')}
                    disabled={!canGoBack || isBusy}
                    onClick={() => setWeekStart(current => addDays(current, -7))}
                >
                    <HugeiconsIcon icon={ArrowLeft01IconStroke} size={20} />
                </IconButton>
                <Typography
                    id={rangeId}
                    variant="subtitle1"
                    aria-live="polite"
                    sx={{ textAlign: 'center' }}
                >
                    {weekRange(weekStart)}
                </Typography>
                <IconButton
                    aria-label={T('Next week')}
                    disabled={isBusy}
                    onClick={() => setWeekStart(current => addDays(current, 7))}
                >
                    <HugeiconsIcon icon={ArrowRight01IconStroke} size={20} />
                </IconButton>
            </Stack>
            {content}
        </Box>
    );
}
